import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  connectSocketClient,
  disconnectSocketClient,
  getSocketClient,
} from '../services/socketClient';

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const sessionReady = isAuthenticated && !loading;
  const [socket, setSocket] = useState(() => getSocketClient());
  const [isConnected, setIsConnected] = useState(() => Boolean(getSocketClient()?.connected));
  const [connectionError, setConnectionError] = useState(null);

  useEffect(() => {
    if (!sessionReady) {
      disconnectSocketClient();
      setSocket(null);
      setIsConnected(false);
      setConnectionError(null);
      return undefined;
    }

    let cancelled = false;

    const attachSocketListeners = (activeSocket) => {
      const handleConnect = () => {
        if (cancelled) return;
        setIsConnected(true);
        setConnectionError(null);
      };

      const handleDisconnect = () => {
        if (cancelled) return;
        setIsConnected(false);
      };

      const handleConnectError = (error) => {
        if (cancelled) return;
        setConnectionError(error?.message || 'Socket connection failed');
        setIsConnected(false);
      };

      activeSocket.on('connect', handleConnect);
      activeSocket.on('disconnect', handleDisconnect);
      activeSocket.on('connect_error', handleConnectError);

      setIsConnected(activeSocket.connected);

      return () => {
        activeSocket.off('connect', handleConnect);
        activeSocket.off('disconnect', handleDisconnect);
        activeSocket.off('connect_error', handleConnectError);
      };
    };

    const connect = async () => {
      try {
        const activeSocket = await connectSocketClient();
        if (cancelled) {
          activeSocket?.removeAllListeners();
          activeSocket?.disconnect();
          return undefined;
        }

        if (!activeSocket) {
          setSocket(null);
          setIsConnected(false);
          return undefined;
        }

        setSocket(activeSocket);
        return attachSocketListeners(activeSocket);
      } catch (error) {
        if (cancelled) return undefined;
        setConnectionError(error?.message || 'Socket connection failed');
        setSocket(null);
        setIsConnected(false);
        return undefined;
      }
    };

    let detachSocketListeners;

    void connect().then((detach) => {
      detachSocketListeners = detach;
    });

    return () => {
      cancelled = true;
      detachSocketListeners?.();
      disconnectSocketClient();
      setSocket(null);
      setIsConnected(false);
      setConnectionError(null);
    };
  }, [sessionReady]);

  const reconnect = useCallback(async () => {
    disconnectSocketClient();
    setSocket(null);
    setIsConnected(false);
    setConnectionError(null);

    const activeSocket = await connectSocketClient();
    if (!activeSocket) {
      return null;
    }

    setSocket(activeSocket);
    setIsConnected(activeSocket.connected);
    return activeSocket;
  }, []);

  const value = useMemo(
    () => ({
      socket,
      isConnected,
      connectionError,
      reconnect,
    }),
    [socket, isConnected, connectionError, reconnect],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketProvider;
